import React, { useEffect, useState } from "react";
import {
  RepairCriteriaData,
  RepairCriteriaReportResponse,
  RepairItem,
} from "./type/datatypes";
import { customer_agreement_get_customer_report } from "../../../API/api";
import customFetch from "../../../API/CustomFetch";
import showToastMessage from "../../../Toastify/Index";
import { CopyTableButton } from "../../../layouts/copy-to-clipboard/CopyTableButton";

interface RepairCriteriaReportProps {
  vehicleProfileId: string;
}

const InspectionSection = ({
  title,
  data,
}: {
  title: string;
  data?: RepairItem[] | null;
}) => {
  if (!data || data.length === 0) return null;

  const mid = Math.ceil(data.length / 2);
  const left = data.slice(0, mid);
  const right = data.slice(mid);

  const gridCols = "2fr 1fr 2fr 1fr";

  return (
    <div className="bg-white p-4 border rounded-lg">
      {/* TITLE */}
      <div className="font-medium text-black">{title}</div>

      {/* INNER BOX */}
      <div className="border rounded-lg my-2">
        {/* HEADER */}
        <div
          className="hidden md:grid bg-white text-[#00AA13] text-sm font-medium p-4 gap-6"
          style={{ gridTemplateColumns: gridCols }}
        >
          <div>List of Items</div>
          <div className="text-right">Response</div>
          <div className="pl-4">List of Items</div>
          <div className="text-right">Response</div>
        </div>

        {/* ROWS */}
        <div className="bg-white  md:hidden">
          <div className="space-y-3 rounded-lg bg-[#F4F7F4] p-3">
            {data.map((item, index) => (
              <div key={index} className="">
                <div className="flex items-start gap-2 text-black font-medium">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 shrink-0"></span>
                  <span>{item.Question}</span>
                </div>
                <div className="mt-1 pl-4 text-black break-words">
                  {item.Response || "-"}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="hidden md:block space-y-3 text-sm bg-[#F4F7F4] p-4">
          {Array.from({ length: mid }).map((_, i) => (
            <div
              key={i}
              className="grid items-center"
              style={{ gridTemplateColumns: gridCols }}
            >
              {/* LEFT */}
              <div className="flex items-center gap-2 text-black">
                <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                {left[i]?.Question}
              </div>
              <div className="text-right text-black">
                {left[i]?.Response || "-"}
              </div>

              {/* RIGHT */}
              <div className="flex items-center gap-2 text-right pl-8">
                {right[i] ? (
                  <>
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                    {right[i]?.Question}
                  </>
                ) : null}
              </div>
              <div className="text-right text-black">
                {right[i]?.Response || (right[i] ? "-" : "")}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PlainRow = ({
  label,
  value,
  color,
}: {
  label: string;
  value: any;
  color?: string;
}) => (
  <div
    className="
      grid grid-cols-2 gap-2 items-start text-sm
      sm:flex sm:justify-between sm:items-start sm:gap-4
    "
  >
    {/* Label */}
    <span className="text-black font-medium break-words">{label}</span>

    {/* Value */}
    <span className={`text-black font-medium break-words text-right ${color}`}>
      {value ?? "N/A"}
    </span>
  </div>
);

const TableRow = ({
  label,
  value,
  color,
}: {
  label: string;
  value: any;
  color?: string;
}) => (
  <tr className="text-sm">
    <td className="text-black font-medium break-words py-2 px-4">{label}</td>

    <td
      className={`text-black font-medium break-words text-right py-2 px-4 ${color}`}
    >
      {value ?? "N/A"}
    </td>
  </tr>
);

const RepairCriteriaReport: React.FC<RepairCriteriaReportProps> = ({
  vehicleProfileId,
}) => {
  const [repairData, setRepairData] = useState<RepairCriteriaData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const url = customer_agreement_get_customer_report(
        vehicleProfileId,
        "54",
      );

      const res: RepairCriteriaReportResponse = await customFetch(url);

      if (res?.SUCCESS) {
        // ✅ HANDLE BOTH CASES
        if (Array.isArray(res.DATA)) {
          setRepairData(null); // no data case
        } else {
          setRepairData(res.DATA);
        }
      } else {
        showToastMessage(false, "Failed to fetch report");
      }
    } catch {
      showToastMessage(false, "Error fetching report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vehicleProfileId) fetchReport();
  }, []);

  return (
    <div className="flex flex-col gap-y-6">
      <div className="bg-[#FFFFFF] rounded-lg p-4 md:p-5 shadow-sm">
        <div className="flex justify-between pb-2">
          <h2 className="font-semibold text-black mb-2">
            Mandatory Requirements
          </h2>
          <CopyTableButton
            data={repairData?.mandatoryAddons || []}
            columns={[
              {
                header: "Requirement",
                accessor: (row) => row.Requirement,
              },
              {
                header: "Price",
                accessor: (row) => row.Cost,
              },
            ]}
          />
        </div>

        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full border-collapse">
            {/* ✅ THIS is critical for Excel */}
            <thead className="bg-[#F4F7F4] text-[#00AA13]">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Requirement</th>
                <th className="text-right px-4 py-2 font-medium">Price</th>
              </tr>
            </thead>

            <tbody>
              {repairData?.mandatoryAddons?.length === 0 && (
                <tr>
                  <td
                    colSpan={2}
                    className="text-sm text-gray-500 text-center py-4"
                  >
                    No mandatory requirements
                  </td>
                </tr>
              )}

              {repairData?.mandatoryAddons?.map((item, index) => (
                <TableRow
                  key={index}
                  label={item.Requirement || ""}
                  value={`₹ ${item.Cost}`}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-[#FFFFFF] rounded-lg p-4 md:p-5 shadow-sm">
        <div className="flex justify-between pb-2">
          {" "}
          <h2 className="font-semibold text-black mb-2">
            Optional Requirements
          </h2>
          <CopyTableButton
            data={repairData?.optionalAddons || []}
            columns={[
              {
                header: "Requirement",
                accessor: (row) => row.Requirement,
              },
              {
                header: "Price",
                accessor: (row) => row.Cost,
              },
            ]}
          />
        </div>

        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full border-collapse">
            {/* ✅ THIS is critical for Excel */}
            <thead className="bg-[#F4F7F4] text-[#00AA13]">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Requirement</th>
                <th className="text-right px-4 py-2 font-medium">Price</th>
              </tr>
            </thead>

            <tbody>
              {repairData?.optionalAddons?.length === 0 && (
                <tr>
                  <td
                    colSpan={2}
                    className="text-sm text-gray-500 text-center py-4"
                  >
                    No optional requirements
                  </td>
                </tr>
              )}

              {repairData?.optionalAddons?.map((item, index) => (
                <TableRow
                  key={index}
                  label={item.Requirement || ""}
                  value={`₹ ${item.Cost}`}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-[#FFFFFF] rounded-lg p-4 md:p-5 shadow-sm">
        <h2 className="font-semibold text-black mb-2"> Payment Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
          <div className="bg-white border rounded-lg">
            <div className="bg-[#F4F7F4] flex flex-row justify-between text-[#00AA13]">
              <div className=" px-4 py-2 font-medium rounded-t-lg">
                Requirement
              </div>
              <div className="px-4 py-2 font-medium rounded-t-lg">Price</div>
            </div>

            <div className="px-4 py-2 space-y-3">
              <PlainRow
                key={1}
                label={"Mandatory Cost"}
                value={`₹ ${repairData?.mandatoryTotal ?? 0}`}
              />
            </div>
            <div className="px-4 py-2 space-y-3">
              <PlainRow
                key={1}
                label={"Optional Cost"}
                value={`₹ ${repairData?.optionalTotal ?? 0}`}
              />
            </div>
            <div className="px-4 py-2 space-y-3 bg-[#F4F7F4]  text-[#00AA13]">
              <PlainRow
                key={1}
                label={"Total Price"}
                color="text-green-700 font-bold text-lg"
                value={`
                  ₹
                  ${
                    (repairData?.mandatoryTotal ?? 0) +
                    (repairData?.optionalTotal ?? 0)
                  }`}
              />
            </div>
          </div>
        </div>
      </div>

      <InspectionSection
        title="Basic Repair Check"
        data={repairData?.repairData}
      />
    </div>
  );
};

export default RepairCriteriaReport;
